import logger from './logger';
import { TypeConverters } from '../config/TypeConfig';

/**
 * LocalStorageManager - 统一管理本地存储操作的单例类
 */
class LocalStorageManager {
  // 存储键名常量
  static Keys = {
    // 认证相关
    AUTH_TOKEN: 'authToken',
    USER_INFO: 'userInfo',
    DATA_SYNCED: 'dataSynced',
    SYNC_IN_PROGRESS: 'syncInProgress',
    
    // 导航相关
    LAST_NAV: 'lastSelectedNav',
    LAST_CATEGORY: 'lastSelectedCategory',
    NAV_ITEMS: 'sideNavItems',
    
    // Dock相关
    DOCK_RECENT_APPS: 'dockRecentApps',
    DOCK_PINNED_APPS: 'dockPinnedApps',
    WEBSITE_CLICK_COUNT: 'websiteClickCount',
    
    // 设置相关
    WALLPAPER: 'selectedWallpaper',
    SEARCH_ENGINE: 'searchEngine',
    ENABLE_LOGS: 'enableLogs',
    START_PAGE_PROMPT: 'hasPromptedStartPage',
    AUTO_FOCUS_SEARCH: 'autoFocusSearch',
    SHOW_SEARCH: 'showSearch',
    SHOW_QUICK_SEARCH: 'showQuickSearch'
  };

  // 网站数据相关的键名常量
  static WEBSITE_KEYS = {
    HOME_WEBSITES: 'homeWebsites',
    NAV_WEBSITES_PREFIX: 'navWebsites_'
  };

  /**
   * 获取单例实例
   */
  static getInstance() {
    if (!LocalStorageManager.instance) {
      LocalStorageManager.instance = new LocalStorageManager();
    }
    return LocalStorageManager.instance;
  }

  /**
   * 设置数据到localStorage
   * @param {string} key - 存储键名
   * @param {*} value - 要存储的值
   * @returns {boolean} 是否成功
   */
  setItem(key, value) {
    try {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
      localStorage.setItem(key, stringValue);
      logger.log(`[LocalStorage] 保存数据成功: ${key}`);
      return true;
    } catch (error) {
      logger.error(`[LocalStorage] 保存数据失败: ${key}`, error);
      return false;
    }
  }

  /**
   * 从localStorage获取数据
   * @param {string} key - 存储键名
   * @param {boolean} [parseJson=false] - 是否需要解析JSON
   * @returns {*} 存储的值
   */
  getItem(key, parseJson = false) {
    try {
      const value = localStorage.getItem(key);
      if (value && parseJson) {
        return JSON.parse(value);
      }
      return value;
    } catch (error) {
      logger.error(`[LocalStorage] 获取数据失败: ${key}`, error);
      return null;
    }
  }

  /**
   * 从localStorage删除数据
   * @param {string} key - 存储键名
   * @returns {boolean} 是否成功
   */
  removeItem(key) {
    try {
      localStorage.removeItem(key);
      logger.log(`[LocalStorage] 删除数据成功: ${key}`);
      return true;
    } catch (error) {
      logger.error(`[LocalStorage] 删除数据失败: ${key}`, error);
      return false;
    }
  }

  /**
   * 清空localStorage
   * @returns {boolean} 是否成功
   */
  clear() {
    try {
      localStorage.clear();
      logger.log('[LocalStorage] 清空所有数据成功');
      return true;
    } catch (error) {
      logger.error('[LocalStorage] 清空所有数据失败', error);
      return false;
    }
  }

  // ============ 认证相关方法 ============
  
  /**
   * 保存认证token
   * @param {string} token 
   */
  setAuthToken(token) {
    return this.setItem(LocalStorageManager.Keys.AUTH_TOKEN, token);
  }

  /**
   * 获取认证token
   */
  getAuthToken() {
    return this.getItem(LocalStorageManager.Keys.AUTH_TOKEN);
  }

  /**
   * 保存用户信息
   * @param {Object} userInfo 
   */
  setUserInfo(userInfo) {
    return this.setItem(LocalStorageManager.Keys.USER_INFO, userInfo);
  }

  /**
   * 获取用户信息
   */
  getUserInfo() {
    return this.getItem(LocalStorageManager.Keys.USER_INFO, true);
  }

  /**
   * 设置数据同步状态
   * @param {boolean} synced 
   */
  setDataSynced(synced) {
    return this.setItem(LocalStorageManager.Keys.DATA_SYNCED, synced.toString());
  }

  /**
   * 获取数据同步状态
   */
  isDataSynced() {
    return this.getItem(LocalStorageManager.Keys.DATA_SYNCED) === 'true';
  }

  // ============ 导航相关方法 ============
  
  /**
   * 保存最后选择的导航
   * @param {string} navId 
   */
  setLastSelectedNav(navId) {
    return this.setItem(LocalStorageManager.Keys.LAST_NAV, navId);
  }

  /**
   * 获取最后选择的导航
   */
  getLastSelectedNav() {
    return this.getItem(LocalStorageManager.Keys.LAST_NAV) || 'home';
  }

  /**
   * 保存导航项目
   * @param {Array} items 
   */
  setNavItems(items) {
    return this.setItem(LocalStorageManager.Keys.NAV_ITEMS, items);
  }

  /**
   * 获取导航项目
   */
  getNavItems() {
    return this.getItem(LocalStorageManager.Keys.NAV_ITEMS, true) || [];
  }

  // ============ Dock相关方法 ============
  
  /**
   * 保存最近使用的应用
   * @param {Array} apps 
   */
  setDockRecentApps(apps) {
    return this.setItem(LocalStorageManager.Keys.DOCK_RECENT_APPS, apps);
  }

  /**
   * 获取最近使用的应用
   */
  getDockRecentApps() {
    return this.getItem(LocalStorageManager.Keys.DOCK_RECENT_APPS, true);
  }

  /**
   * 保存固定的应用
   * @param {Array} apps 
   */
  setDockPinnedApps(apps) {
    return this.setItem(LocalStorageManager.Keys.DOCK_PINNED_APPS, apps);
  }

  /**
   * 获取固定的应用
   */
  getDockPinnedApps() {
    return this.getItem(LocalStorageManager.Keys.DOCK_PINNED_APPS, true);
  }

  // ============ 网站点击统计相关方法 ============
  
  /**
   * 记录网站点击次数
   * @param {string} url - 网站URL
   * @param {Object} websiteData - 网站数据对象
   */
  recordWebsiteClick(url, websiteData) {
    try {
      if (!url) {
        logger.warn('[网站统计] URL为空，无法记录点击');
        return false;
      }

      const clickCounts = this.getWebsiteClickCounts();
      const normalizedUrl = this._normalizeUrl(url);
      
      // 如果网站已存在，增加点击次数
      if (clickCounts[normalizedUrl]) {
        clickCounts[normalizedUrl].count += 1;
        clickCounts[normalizedUrl].lastClickTime = Date.now();
      } else {
        // 新网站，初始化点击记录
        clickCounts[normalizedUrl] = {
          count: 1,
          firstClickTime: Date.now(),
          lastClickTime: Date.now(),
          websiteData: websiteData || { url: normalizedUrl }
        };
      }

      this.setItem(LocalStorageManager.Keys.WEBSITE_CLICK_COUNT, clickCounts);
      logger.log(`[网站统计] 记录网站点击: ${normalizedUrl}, 总次数: ${clickCounts[normalizedUrl].count}`);
      return true;
    } catch (error) {
      logger.error('[网站统计] 记录网站点击失败:', error);
      return false;
    }
  }

  /**
   * 获取网站点击统计
   * @returns {Object} 网站点击统计对象
   */
  getWebsiteClickCounts() {
    return this.getItem(LocalStorageManager.Keys.WEBSITE_CLICK_COUNT, true) || {};
  }

  /**
   * 获取最常用的网站列表
   * @param {number} limit - 返回数量限制，默认3
   * @returns {Array} 最常用网站数组
   */
  getMostUsedWebsites(limit = 3) {
    try {
      const clickCounts = this.getWebsiteClickCounts();
      const websites = Object.entries(clickCounts)
        .map(([url, data]) => ({
          url,
          count: data.count,
          lastClickTime: data.lastClickTime,
          websiteData: data.websiteData
        }))
        .sort((a, b) => {
          // 先按点击次数排序，次数相同时按最后点击时间排序
          if (b.count !== a.count) {
            return b.count - a.count;
          }
          return b.lastClickTime - a.lastClickTime;
        })
        .slice(0, limit)
        .map(item => item.websiteData);

      logger.log(`[网站统计] 获取最常用网站: ${websites.length}个`);
      return websites;
    } catch (error) {
      logger.error('[网站统计] 获取最常用网站失败:', error);
      return [];
    }
  }

  /**
   * 清除过期的网站点击记录
   * @param {number} daysToKeep - 保留天数，默认30天
   */
  clearExpiredClickRecords(daysToKeep = 30) {
    try {
      const clickCounts = this.getWebsiteClickCounts();
      const cutoffTime = Date.now() - (daysToKeep * 24 * 60 * 60 * 1000);
      let removedCount = 0;

      Object.keys(clickCounts).forEach(url => {
        if (clickCounts[url].lastClickTime < cutoffTime) {
          delete clickCounts[url];
          removedCount++;
        }
      });

      if (removedCount > 0) {
        this.setItem(LocalStorageManager.Keys.WEBSITE_CLICK_COUNT, clickCounts);
        logger.log(`[网站统计] 清除过期记录: ${removedCount}个网站`);
      }

      return removedCount;
    } catch (error) {
      logger.error('[网站统计] 清除过期记录失败:', error);
      return 0;
    }
  }

  /**
   * 标准化URL，用于去重
   * @param {string} url - 原始URL
   * @returns {string} 标准化后的URL
   * @private
   */
  _normalizeUrl(url) {
    try {
      // 移除协议前缀
      let normalized = url.replace(/^https?:\/\//, '');
      // 移除www前缀
      normalized = normalized.replace(/^www\./, '');
      // 移除末尾的斜杠
      normalized = normalized.replace(/\/$/, '');
      // 转换为小写
      normalized = normalized.toLowerCase();
      return normalized;
    } catch (error) {
      logger.error('[网站统计] URL标准化失败:', error);
      return url;
    }
  }

  // ============ 设置相关方法 ============
  
  /**
   * 保存壁纸设置
   * @param {string} wallpaper 
   */
  setWallpaper(wallpaper) {
    return this.setItem(LocalStorageManager.Keys.WALLPAPER, wallpaper);
  }

  /**
   * 获取壁纸设置
   */
  getWallpaper() {
    return this.getItem(LocalStorageManager.Keys.WALLPAPER);
  }

  /**
   * 保存搜索引擎设置
   * @param {string} engine 
   */
  setSearchEngine(engine) {
    return this.setItem(LocalStorageManager.Keys.SEARCH_ENGINE, engine);
  }

  /**
   * 获取搜索引擎设置
   */
  getSearchEngine() {
    return this.getItem(LocalStorageManager.Keys.SEARCH_ENGINE);
  }

  /**
   * 设置日志开关
   * @param {boolean} enabled 
   */
  setLogsEnabled(enabled) {
    return this.setItem(LocalStorageManager.Keys.ENABLE_LOGS, enabled.toString());
  }

  /**
   * 获取日志开关状态
   */
  isLogsEnabled() {
    return this.getItem(LocalStorageManager.Keys.ENABLE_LOGS) === 'true';
  }

  /**
   * 设置进入导航自动聚焦搜索
   * @param {boolean} enabled 
   */
  setAutoFocusSearch(enabled) {
    return this.setItem(LocalStorageManager.Keys.AUTO_FOCUS_SEARCH, enabled.toString());
  }

  /**
   * 获取进入导航自动聚焦搜索状态
   */
  getAutoFocusSearch() {
    const value = this.getItem(LocalStorageManager.Keys.AUTO_FOCUS_SEARCH);
    return value === null ? true : value === 'true';
  }

  /**
   * 设置搜索功能展示
   * @param {boolean} enabled 
   */
  setShowSearch(enabled) {
    return this.setItem(LocalStorageManager.Keys.SHOW_SEARCH, enabled.toString());
  }

  /**
   * 获取搜索功能展示状态
   */
  getShowSearch() {
    const value = this.getItem(LocalStorageManager.Keys.SHOW_SEARCH);
    return value === null ? true : value === 'true';
  }

  /**
   * 设置站内网站快捷搜索展示
   * @param {boolean} enabled 
   */
  setShowQuickSearch(enabled) {
    return this.setItem(LocalStorageManager.Keys.SHOW_QUICK_SEARCH, enabled.toString());
  }

  /**
   * 获取站内网站快捷搜索展示状态
   */
  getShowQuickSearch() {
    const value = this.getItem(LocalStorageManager.Keys.SHOW_QUICK_SEARCH);
    return value === null ? true : value === 'true';
  }

  /**
   * 处理网站数据中 preferredIconType 的兼容性
   * 将旧版本的字符串值转换为新版本的数字值
   * @param {Array} websites - 网站数据数组
   * @returns {Array} - 处理后的网站数据数组
   * @private
   */
  _processPreferredIconTypeCompatibility(websites) {
    if (!Array.isArray(websites)) {
      return websites;
    }

    return websites.map(website => {
      if (website && typeof website === 'object') {
        // 使用 TypeConfig 的转换器处理兼容性，传入完整的网站数据用于智能推断
        const originalType = website.preferredIconType;
        const convertedType = TypeConverters.convertIconType(originalType, website);
        
        // 如果值发生了变化，记录日志
        if (originalType !== convertedType) {
          logger.log(`[兼容处理] 将网站 ${website.name || website.id} 的 preferredIconType 从 ${originalType} 转换为 ${convertedType}`);
        }
        
        // 创建新对象而不是直接修改原对象，避免只读属性错误
        const updatedWebsite = {
          ...website,
          preferredIconType: convertedType,
          // 确保其他必要字段的默认值
          bgColor: website.bgColor || 'white',
          iconColor: website.iconColor || '#ffffff'
        };
        
        return updatedWebsite;
      }
      return website;
    });
  }

  /**
   * 获取指定导航的网站数据
   * @param {string} navId - 导航ID
   * @returns {Array|null} - 网站数据数组或null
   */
  getWebsitesForNav(navId) {
    try {
      const key = navId === 'home' ? 
        LocalStorageManager.WEBSITE_KEYS.HOME_WEBSITES : 
        `${LocalStorageManager.WEBSITE_KEYS.NAV_WEBSITES_PREFIX}${navId}`;
      
      const data = this.getItem(key);
      if (!data) return null;  // 如果没有数据，返回空数组而不是null

      try {
        let websites = JSON.parse(data);
        if (!Array.isArray(websites)) {
          logger.error(`导航 ${navId} 的数据格式不正确，应为数组`);
          this.removeItem(key);
          return null;  // 返回空数组而不是null
        }

        // 处理 preferredIconType 兼容性
        websites = this._processPreferredIconTypeCompatibility(websites);

        return websites;
      } catch (error) {
        logger.error(`解析导航 ${navId} 的数据失败:`, error);
        this.removeItem(key);  // 删除无效数据
        return null;  // 返回空数组
      }
    } catch (error) {
      logger.error(`获取导航 ${navId} 的网站数据失败:`, error);
      return null;  // 返回空数组而不是null
    }
  }

  /**
   * 保存指定导航的网站数据
   * @param {string} navId - 导航ID
   * @param {Array} websites - 网站数据数组
   * @returns {boolean} - 是否保存成功
   */
  saveWebsitesForNav(navId, websites) {
    try {
      if (!Array.isArray(websites)) {
        throw new Error('网站数据必须是数组');
      }

      // 在保存前处理 preferredIconType 兼容性，确保保存的数据格式正确
      const processedWebsites = this._processPreferredIconTypeCompatibility(websites);

      const key = navId === 'home' ? 
        LocalStorageManager.WEBSITE_KEYS.HOME_WEBSITES : 
        `${LocalStorageManager.WEBSITE_KEYS.NAV_WEBSITES_PREFIX}${navId}`;
      
      this.setItem(key, JSON.stringify(processedWebsites));
      logger.log(`已将导航 ${navId} 的 ${processedWebsites.length} 个网站保存到存储`);
      return true;
    } catch (error) {
      logger.error(`保存导航 ${navId} 的网站数据失败:`, error);
      return false;
    }
  }

  /**
   * 删除指定导航的网站数据
   * @param {string} navId - 导航ID
   * @returns {boolean} - 是否删除成功
   */
  removeWebsitesForNav(navId) {
    try {
      const key = navId === 'home' ? 
        LocalStorageManager.WEBSITE_KEYS.HOME_WEBSITES : 
        `${LocalStorageManager.WEBSITE_KEYS.NAV_WEBSITES_PREFIX}${navId}`;
      
      this.removeItem(key);
      logger.log(`已删除导航 ${navId} 的网站数据`);
      return true;
    } catch (error) {
      logger.error(`删除导航 ${navId} 的网站数据失败:`, error);
      return false;
    }
  }
}

// 导出单例实例
export default LocalStorageManager.getInstance();

// 导出键名常量
export const StorageKeys = LocalStorageManager.Keys;
